/**
 * Centralized compensation-related configuration (E13-4).
 * Following OCP principle - extend by adding to configs, not modifying code.
 * Colors aligned with Medley Design System (see /docs/STYLE_GUIDE.md).
 */

import type { StatusConfig } from './statusConfig';
import { parseDateOnly } from '../utils/dateHelpers';
import { formatDateOnly } from '../utils/formatters';

/**
 * CM View: Status badges for compensation calculation results.
 * Includes synthetic 'adjusted' and 'fully_paid' statuses used in UI only.
 */
export const COMPENSATION_STATUS_CONFIG: Record<string, StatusConfig> = {
  system_calculated: { label: 'System Calculated', color: 'gray', variant: 'light' },
  calculated: { label: 'Calculated', color: 'blue', variant: 'light' },
  confirmed: { label: 'Confirmed', color: 'medleyGreen', variant: 'light' },
  pending: { label: 'Pending', color: 'gray', variant: 'light' },
  adjusted: { label: 'Adjusted', color: 'warningAmber', variant: 'filled' },
  fully_paid: { label: 'Fully Paid', color: 'medleyGreen', variant: 'light' },
  payments_scheduled: { label: 'Payments Scheduled', color: 'gray', variant: 'light' },
  partially_paid: { label: 'Partially Paid', color: 'warningAmber', variant: 'light' },
  // MED-1383: the unpaid stages of a cohort summary.
  payments_pending: { label: 'Not Sent to Bill.com', color: 'gray', variant: 'light' },
  awaiting_approval: { label: 'Awaiting Approval', color: 'warningAmber', variant: 'light' },
  approved: { label: 'Approved (not scheduled)', color: 'statusInPrep', variant: 'filled' },
  payments_failed: { label: 'Payments Failed', color: 'dangerRed', variant: 'light' },
};

export interface PaymentTrackLabel {
  label: string;
  color: string;
  variant: 'filled' | 'light' | 'outline';
}

/**
 * CM View: Payment track badges (MONTHLY / NET 10).
 */
export const CM_PAYMENT_TRACK_LABELS: Record<string, PaymentTrackLabel> = {
  flat_rate_monthly: { label: 'MONTHLY', color: 'medleyGreen', variant: 'light' },
  attendance_dependent_net_10: { label: 'NET 10', color: 'warningAmber', variant: 'light' },
};

/**
 * Coach View: Payment track badges (GUARANTEED / ATTENDANCE BASED).
 * Different labels from CM view for coach-facing language.
 */
export const COACH_PAYMENT_TRACK_LABELS: Record<string, PaymentTrackLabel> = {
  flat_rate_monthly: { label: 'GUARANTEED', color: 'medleyGreen', variant: 'light' },
  // MED-1383: the track says how the payment is earned, not its rate unit. "Per event"
  // contradicted a 1:1 paid per session and an Orientation paid per hour.
  attendance_dependent_net_10: {
    label: 'ATTENDANCE BASED',
    color: 'warningAmber',
    variant: 'light',
  },
};

/**
 * Payment status badges for individual payment records. The one shared map for every
 * payment status badge in the staff views: the CM's Flat Rate table, Sent to Bill.com
 * list and audit drawer. Coaches use COACH_PAYMENT_STATUS_CONFIG (MED-1405). Read it
 * through getPaymentStatusConfig so the scheduled date lands on the label. Unknown
 * statuses fall back to 'pending'.
 * COACH_PAYMENT_TRACK_LABELS is consumed by the same coach Payment History list.
 */
export const PAYMENT_STATUS_CONFIG: Record<string, StatusConfig> = {
  overdue: { label: 'Overdue', color: 'dangerRed', variant: 'light' },
  pending: { label: 'Pending', color: 'warningAmber', variant: 'light' },
  // MED-1306 / MDF-174: the bill is in Bill.com and no human has approved it yet.
  awaiting_approval: { label: 'Awaiting approval', color: 'warningAmber', variant: 'light' },
  // MED-1383: approved in Bill.com, but nobody has scheduled the payment there yet.
  approved: { label: 'Approved (not scheduled)', color: 'statusInPrep', variant: 'filled' },
  submitted: { label: 'Submitted', color: 'gray', variant: 'light' },
  processing: { label: 'Processing', color: 'gray', variant: 'light' },
  paid: { label: 'Paid', color: 'medleyGreen', variant: 'filled' },
  received: { label: 'Received', color: 'medleyGreen', variant: 'light' },
  upcoming: { label: 'Upcoming', color: 'blue', variant: 'light' },
  failed: { label: 'Failed', color: 'dangerRed', variant: 'filled' },
  scheduled: { label: 'Scheduled', color: 'medleyGreen', variant: 'light' },
  voided: { label: 'Voided', color: 'gray', variant: 'outline' },
  // MED-1405: SGOS voided the row, but Bill.com did not void anything. See
  // getStaffPaymentStatus.
  cancelled: { label: 'Cancelled in SGOS', color: 'gray', variant: 'outline' },
};

/**
 * MED-1405: a coach sees two statuses. The API maps every Bill.com status to one of
 * them and never sends voided rows. Staff views keep PAYMENT_STATUS_CONFIG.
 */
export const COACH_PAYMENT_STATUS_CONFIG: Record<string, StatusConfig> = {
  paid: { label: 'Paid', color: 'medleyGreen', variant: 'filled' },
  // MED-1405 QA: gray, because amber made it look the same as the Attendance based badge.
  not_yet_paid: { label: 'Not yet paid', color: 'gray', variant: 'light' },
};

/**
 * MED-1405: "Voided" is a Bill.com status, so staff see it only when Bill.com acted:
 * the bill is confirmed archived, or a Bill.com payment existed to void. Any other
 * voided row was cancelled by SGOS alone (no bill yet, the bill stays live for other
 * rows, or Bill.com refused the archive).
 */
export function getStaffPaymentStatus(payment: {
  status: string;
  bill_archived_at?: string | null;
  billcom_payment_id?: string | null;
}): string {
  if (payment.status !== 'voided') return payment.status;
  return payment.bill_archived_at || payment.billcom_payment_id ? 'voided' : 'cancelled';
}

/**
 * MED-1383: the unit of a line item's rate, as the calculation uses it. Orientation
 * and Wrap-Up (and the legacy Cohort Event) are paid per hour of the event; the
 * rate card says /hr. Huddle and Immersion are paid per event; the rate card says
 * /event (RateCardDisplay FLAT_RATE_CONFIG). Other rate-based items are paid per
 * session.
 */
const PER_HOUR_LINE_ITEM_KEYS = new Set(['orientation', 'wrap_up', 'cohort_event']);
const PER_EVENT_LINE_ITEM_KEYS = new Set(['huddle', 'immersion']);

export function getLineItemRateUnit(itemKey: string): 'per hour' | 'per event' | 'per session' {
  if (PER_HOUR_LINE_ITEM_KEYS.has(itemKey)) return 'per hour';
  return PER_EVENT_LINE_ITEM_KEYS.has(itemKey) ? 'per event' : 'per session';
}

/**
 * MED-1383: which session a Net 10 session row pays, for example
 * "Apr 17, 2026 · Pat Participant". Two 1:1 rows of one coach have the same label
 * and amount, so the date and the participant tell them apart. Null for a cohort
 * event or a debrief row.
 */
export function getNet10SessionDetail(payment: {
  event_reference_type: string;
  event_local_date?: string;
  participant_name?: string | null;
}): string | null {
  // MED-1346: a paid no-show or late change is the same 1:1 work.
  if (payment.event_reference_type !== 'session' && payment.event_reference_type !== 'one_on_one_miss') {
    return null;
  }
  const parts = [
    payment.event_local_date ? formatDateOnly(payment.event_local_date) : null,
    payment.participant_name || null,
  ].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * MED-1383: the fixed order of a coach's plan lines, the order of LINE_ITEM_KEYS in
 * medley-api. The API orders lines by category and created_at, and one calculation
 * writes every line at the same instant, so the order differed from coach to coach.
 */
const LINE_ITEM_ORDER = [
  'group_session_flat',
  'huddle',
  'immersion',
  'orientation',
  'wrap_up',
  'cohort_event',
  'one_on_one',
  'two_on_one',
  'workshop',
  'in_person_half_day',
  'in_person_full_day',
  'coach_debrief',
];

/** Sort plan lines by LINE_ITEM_ORDER, then by label. An unknown key goes last. */
export function compareLineItems(
  a: { item_key: string; label: string },
  b: { item_key: string; label: string },
): number {
  const rank = (key: string) => {
    const index = LINE_ITEM_ORDER.indexOf(key);
    return index === -1 ? LINE_ITEM_ORDER.length : index;
  };
  return (
    rank(a.item_key) - rank(b.item_key) ||
    a.label.localeCompare(b.label, undefined, { numeric: true })
  );
}

/**
 * MED-1383: a voided payment is history, not an error. Its row is dimmed, it sorts
 * after the live rows, and its note is gray. A failed row keeps the red note.
 */
export function isHistoryPayment(status: string): boolean {
  return status === 'voided';
}

export const HISTORY_ROW_STYLE = { opacity: 0.6 } as const;

export function getPaymentNoteColor(status: string): 'dimmed' | 'dangerRed' {
  return isHistoryPayment(status) ? 'dimmed' : 'dangerRed';
}

/**
 * MED-1383: width for a table's Status column. At full width it holds the longest
 * one-line label ("SCHEDULED (OCT 20)"). On a narrow screen the vw part wins, and
 * WRAPPING_BADGE_STYLES wraps the label instead.
 */
export const STATUS_COLUMN_STYLE = { textAlign: 'center', width: 'min(170px, 13vw)' } as const;

/**
 * MED-1383: Badge styles that let a status label wrap instead of ending in an
 * ellipsis. "Approved (not scheduled)" read "APPR..." in a narrow column. Pass as
 * `styles` on every payment status Badge.
 */
export const WRAPPING_BADGE_STYLES = {
  root: { height: 'auto', maxWidth: '100%', paddingTop: 2, paddingBottom: 2 },
  label: {
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
    lineHeight: 1.3,
    textAlign: 'center',
  },
} as const;

/**
 * Badge config for a payment status. A scheduled payment with a Bill.com date reads
 * "Scheduled (Oct 21)", with the year only when it is not the current year.
 * The date is a calendar date, so parseDateOnly keeps the day: new Date() on
 * "2026-10-21" is UTC midnight and shows Oct 20 west of UTC.
 */
export function getPaymentStatusConfig(
  status: string,
  scheduledDate?: string | null,
): StatusConfig {
  const config = PAYMENT_STATUS_CONFIG[status] ?? PAYMENT_STATUS_CONFIG.pending;
  if (status !== 'scheduled') return config;
  const date = parseDateOnly(scheduledDate);
  if (!date || isNaN(date.getTime())) return config;
  const label = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== new Date().getFullYear() && { year: 'numeric' }),
  });
  return { ...config, label: `${config.label} (${label})` };
}

/**
 * Derive the display status for a compensation result, accounting for adjustments.
 * Returns 'adjusted' when status is 'confirmed' but line items have adjustments.
 */
export function getCompensationDisplayStatus(
  status: string,
  hasAdjustments: boolean,
): string {
  if (hasAdjustments && status === 'confirmed') return 'adjusted';
  return status;
}
